import React, { useEffect, useMemo } from 'react';
import { Container } from 'react-bootstrap';
import { useLocation, useNavigate } from 'react-router-dom';
import { Applications } from '../../../@types/configuration';
import ExecutionHistoryDetailsCardComponent from '../../../components/shared/execution/history-details-card';
import useExecutions from '../../../resources/executions/executions-hook';

const formatExecutionTime = (diffMs: number) => {
  if (diffMs == null || diffMs < 0) {
    return null;
  }

  if (diffMs < 1000) {
    return `${diffMs}ms`;
  }

  const diffSeconds = diffMs / 1000;

  const formattedSeconds = diffSeconds.toLocaleString('en-US', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 1,
  });

  return `${formattedSeconds}s`;
};

function ExecutionHistoryDetails() {
  const location = useLocation();
  const navigate = useNavigate();
  const searchParams = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const { executionDetails, fetchExecutionDetails } = useExecutions();
  const wannaBeProcessorLog = useMemo(
    () => ({
      executionId: searchParams.get('executionId') ?? undefined,
      sharedId: searchParams.get('sharedId') ?? undefined,
      application: (searchParams.get('application') as Applications) ?? undefined,
      processorName: searchParams.get('processorName') ?? undefined,
      startDt: searchParams.get('startDt') ?? undefined,
      endDt: searchParams.get('endDt') ?? undefined,
    }),
    [searchParams],
  );

  const referrer = useMemo(() => searchParams.get('referrer') ?? undefined, [searchParams]);

  const totalExecutionTime = useMemo(() => {
    const { startDt, endDt } = wannaBeProcessorLog;

    if (!startDt || !endDt) {
      return null;
    }

    try {
      const startTime = new Date(startDt).getTime();
      const endTime = new Date(endDt).getTime();
      const diffMs = endTime - startTime;

      return formatExecutionTime(diffMs);
    } catch (e) {
      console.error('Error calculating the total execution time:', e);
      return null;
    }
  }, [wannaBeProcessorLog]);

  useEffect(() => {
    fetchExecutionDetails(wannaBeProcessorLog);
  }, [fetchExecutionDetails, wannaBeProcessorLog]);

  const handleGoBack = () => {
    if (location.key) {
      navigate(-1);
    } else {
      navigate('/execution-history');
    }
  };

  return (
    <Container>
      <ExecutionHistoryDetailsCardComponent
        execution={wannaBeProcessorLog}
        details={executionDetails.data}
        onBackClick={handleGoBack}
        referrer={referrer}
        totalExecutionTime={totalExecutionTime}
      />
    </Container>
  );
}

export default ExecutionHistoryDetails;
